import React, {useCallback, useMemo, useState} from 'react'
import Skeleton from 'react-loading-skeleton'
import styled from 'styled-components'
import {IMAGE_ASSET} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {useTheme} from 'themes'
import {TreeFotoTreeDetailResponse} from 'types'
import {useDidMount, getFontFamily, getBoxShadow} from 'utils'
import {Paragraph, Image, Icon, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'
import {FototreeDetailContentExtra} from '../content'
import {TreePopupContentProps} from './TreeFototreeModalProps'

const StyledRowContainer = styled.div`
  display: flex;
`

const StyledColumnContainer = styled(StyledRowContainer)`
  flex-direction: column;
`

const StyledContainer = styled(StyledColumnContainer)`
  padding: ${convertUnit(20)};
  min-width: ${convertUnit(200)};
  max-width: ${convertUnit(335)};
  background-color: ${({theme}) => theme.white_1};
  border-radius: ${convertUnit(16)};
  box-shadow: ${({theme}) =>
    getBoxShadow(theme, {verticalOffset: 4, opacity: 0.16})};
`

const StyledHeaderContainer = styled(StyledRowContainer)`
  gap: ${convertUnit(20)};
  margin-bottom: ${convertUnit(12)};
  align-items: center;
  justify-content: center;
`

const StyledTextContainer = styled(StyledColumnContainer)`
  flex: 1;
  gap: ${convertUnit(4)};
`

const StyledDetailContainer = styled(StyledRowContainer)`
  gap: ${convertUnit(12)};
`

const StyledSpan = styled.span`
  font-family: ${getFontFamily('bold')};
`

const StyledImage = styled(Image)`
  width: ${convertUnit(68)};
  aspect-ratio: 0.75;
  border-radius: ${convertUnit(8)};
  object-fit: contain;
`

const StyledHiddenCountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertUnit(4)};
`

const StyledIconExclamation = styled.div`
  display: flex;
  justify-content: center;
  background-color: ${({theme}) => theme.info_5};
  rotate: 180deg;
  border-radius: 50%;
  width: ${convertUnit(10)};
  height: ${convertUnit(10)};
`

export default function TreePopupContent({id, tree}: TreePopupContentProps) {
  const defaultImage = IMAGE_ASSET('tree', 'default.png')
  const {translate} = useTranslation()
  const theme = useTheme()
  const [data, setData] = useState<TreeFotoTreeDetailResponse | undefined>(tree)

  const handleLoadTreeInfo = useCallback(() => {
    requestData('tree_get_fototree_detail', {
      actionType: 'fetch',
      useDefaultMessage: true,
      params: {tree_id: id},
      onRequestSuccess: ({data: fetchData, status}) => {
        if (status === 200) {
          setData(fetchData.result)
        }
      },
    })
  }, [id])

  const handleRenderExtra = useMemo(
    () =>
      data && (
        <FototreeDetailContentExtra
          data={data}
          fontWeight="medium"
          color="black_1"
        />
      ),
    [data],
  )

  useDidMount(() =>
    typeof tree === 'undefined' ? handleLoadTreeInfo() : undefined,
  )

  return data ? (
    <StyledContainer>
      <StyledHeaderContainer>
        <StyledImage
          src={data.url}
          fallbackSrc={defaultImage}
          alt={data.name}
        />
        <StyledTextContainer>
          <Paragraph fontSize="l" fontWeight="bold" lineHeight={22}>
            {data.name}
          </Paragraph>
          <StyledDetailContainer>
            <Paragraph>
              <ParsedText
                parse={[
                  {
                    pattern: /<bold>.*?<\/bold>/,
                    style: {fontFamily: getFontFamily('bold')},
                    renderText: (text) => text.replace(/<bold>|<\/bold>/g, ''),
                  },
                ]}>
                {translate('tree:leafCount', {
                  count: data.count_memory,
                })}
              </ParsedText>
            </Paragraph>
            |
            <Paragraph>
              <StyledSpan>{data.height || 0}</StyledSpan>{' '}
              {translate('tree:treeHeight')}
            </Paragraph>
          </StyledDetailContainer>
          <StyledHiddenCountContainer>
            <StyledIconExclamation>
              <Icon
                type="exclamation-lg"
                size={convertUnit(6)}
                color="white_1"
              />
            </StyledIconExclamation>
            <Paragraph fontWeight="medium" fontSize="xs" color="info_5">
              {translate('tree:leafCountIncludePrivate')}
            </Paragraph>
          </StyledHiddenCountContainer>
        </StyledTextContainer>
      </StyledHeaderContainer>
      {handleRenderExtra}
    </StyledContainer>
  ) : (
    <StyledContainer>
      <StyledHeaderContainer>
        <Skeleton
          width={68}
          height={88}
          borderRadius={8}
          baseColor={theme.gray_1}
          highlightColor={theme.white_4}
        />
        <StyledTextContainer>
          <Paragraph fontSize="l" fontWeight="bold">
            <Skeleton
              borderRadius={17}
              baseColor={theme.gray_1}
              highlightColor={theme.white_4}
            />
          </Paragraph>
          <StyledDetailContainer>
            <Paragraph>
              <Skeleton
                borderRadius={17}
                width={40}
                baseColor={theme.gray_1}
                highlightColor={theme.white_4}
              />
            </Paragraph>
            |
            <Paragraph>
              <Skeleton
                borderRadius={17}
                width={40}
                baseColor={theme.gray_1}
                highlightColor={theme.white_4}
              />
            </Paragraph>
          </StyledDetailContainer>
        </StyledTextContainer>
      </StyledHeaderContainer>
      <Skeleton
        borderRadius={17}
        count={3}
        baseColor={theme.gray_1}
        highlightColor={theme.white_4}
      />
    </StyledContainer>
  )
}
